import React from "react";
import * as Mui from "@mui/material";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRounded from "@mui/icons-material/ErrorOutlineRounded";
import FingerprintOutlined from "@mui/icons-material/FingerprintOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import PlaceOutlined from "@mui/icons-material/PlaceOutlined";
import SearchRounded from "@mui/icons-material/SearchRounded";
import SwapHorizRounded from "@mui/icons-material/SwapHorizRounded";
import {
  designColors,
  designRadii,
  designType,
} from "services/constants/designTokens";
import {
  GlobalFieldLabel,
  globalTextFieldSx,
} from "components/DesignSystem/sharedUiPatterns";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { RequiredMark } from "components/DesignSystem/SettingsPageShell";
import { SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import {
  DEVICE_PURPOSES,
  NEW_DEVICE_MODES,
} from "./organisationDevicesData";

const DANGER = "#DC2626";
const ACTIVE_COLOR = "#16A34A";

// designTheme keeps MUI's default blue primary, so focus rings and selected
// options are set to the app's teal here.
const fieldSx = {
  ...globalTextFieldSx,
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: designColors.tealDark,
  },
};

const MODE_ICONS = {
  "In Only": ArrowForwardRounded,
  "Out Only": ArrowBackRounded,
  "Bi-Directional": SwapHorizRounded,
};
const MODE_ICON_COLORS = {
  "In Only": ACTIVE_COLOR,
  "Out Only": DANGER,
  "Bi-Directional": designColors.slate600,
};
const PURPOSE_ICONS = {
  Attendance: FingerprintOutlined,
  Patrolling: PlaceOutlined,
};

const modeTitle = (mode) =>
  NEW_DEVICE_MODES.find((option) => option.value === mode)?.title || mode;

// One section of the panel: a plain medium-weight title (same size and
// weight as the dashboard card titles) and a helper line.
const Step = ({ title, subtitle, children }) => (
  <Mui.Box component="section" aria-label={title}>
    <Mui.Typography
      component="h3"
      sx={{ fontSize: 15, fontWeight: 500, color: designColors.slate900 }}
    >
      {title}
    </Mui.Typography>
    <Mui.Typography sx={designType.helperText}>{subtitle}</Mui.Typography>
    <Mui.Stack spacing={2} sx={{ mt: 1.75 }}>
      {children}
    </Mui.Stack>
  </Mui.Box>
);

const Field = ({ label, required, error, helperText, children }) => (
  <Mui.Box>
    <GlobalFieldLabel>
      {label}
      {required && <RequiredMark />}
    </GlobalFieldLabel>
    {children}
    {(error || helperText) && (
      <Mui.FormHelperText error={Boolean(error)} sx={{ mt: 0.75, fontSize: 12.5 }}>
        {error || helperText}
      </Mui.FormHelperText>
    )}
  </Mui.Box>
);

// A row of selectable cards behaving as one radio group.
const OptionCards = ({ label, options, value, onChange, icons, iconColors = {}, badgeFor, disabled = false }) => (
  <Mui.Box
    role="radiogroup"
    aria-label={label}
    sx={{
      display: "grid",
      gap: 1.25,
      gridTemplateColumns: {
        xs: "1fr",
        sm: "repeat(" + options.length + ", minmax(0, 1fr))",
      },
    }}
  >
    {options.map((option) => {
      const selected = option.value === value;
      const Icon = icons[option.value];
      return (
        <Mui.ButtonBase
          key={option.value}
          role="radio"
          aria-checked={selected}
          aria-label={option.title}
          disabled={disabled}
          onClick={() => { if (!disabled) onChange(option.value); }}
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: 0.5,
            px: 1.25,
            pt: 2,
            pb: 1.5,
            borderRadius: designRadii.card,
            border:
              "1.5px solid " +
              (selected ? designColors.tealDark : designColors.slate200),
            backgroundColor: selected ? designColors.tealTint : designColors.white,
            "&:hover": { borderColor: designColors.tealDark },
            "&.Mui-focusVisible": {
              outline: "2px solid " + designColors.tealDark,
              outlineOffset: 2,
            },
          }}
        >
          <Mui.Radio
            checked={selected}
            disabled={disabled}
            size="small"
            tabIndex={-1}
            disableRipple
            inputProps={{ "aria-hidden": true }}
            sx={{
              position: "absolute",
              top: 2,
              left: 2,
              pointerEvents: "none",
              color: designColors.slate400,
              "&.Mui-checked": { color: designColors.tealDark },
            }}
          />
          <Icon
            sx={{
              fontSize: 26,
              color: iconColors[option.value] || designColors.slate600,
            }}
          />
          <Mui.Typography
            sx={{ fontSize: 14, fontWeight: 500, color: designColors.slate900 }}
          >
            {option.title}
          </Mui.Typography>
          {badgeFor === option.value && (
            <RequestStatusChip status="Detected" colorCode={designColors.tealDark} />
          )}
          <Mui.Typography sx={designType.captionText}>{option.text}</Mui.Typography>
        </Mui.ButtonBase>
      );
    })}
  </Mui.Box>
);

// Body of the Add Device panel, in three sections: fetch the device from the punch server,
// then configure how it is used here.
//   draft: see emptyNewDevice() in organisationDevicesData
// With `isEdit` the device ID is fixed and there is nothing to fetch.
export const AddDeviceForm = ({ isEdit = false, draft, errors, onChange, onFetch, modeOptions = NEW_DEVICE_MODES }) => {
  const lookup = draft.lookup;
  const found = lookup?.status === "found";
  const DetectedIcon = found ? MODE_ICONS[lookup.mode] : null;

  return (
    <Mui.Stack spacing={3} divider={<Mui.Divider flexItem />}>
      <Step
        title={isEdit || !onFetch ? "Device Information" : "Fetch Device Information"}
        subtitle={
          isEdit
            ? "The device ID registered on the eBioServer / eSSL server."
            : "Enter the device ID configured in the eBioServer / eSSL server."
        }
      >
        <Field
          label="Device ID"
          required
          error={errors.deviceId}
          helperText={
            isEdit
              ? "The device ID cannot be changed. Delete the device and add it again to use a different ID."
              : "Enter the exact device ID / terminal number from the server."
          }
        >
          <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
            <Mui.TextField
              fullWidth
              size="small"
              value={draft.deviceId}
              disabled={isEdit}
              placeholder="e.g. 2"
              onChange={(event) => onChange("deviceId", event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !isEdit && onFetch) onFetch();
              }}
              error={Boolean(errors.deviceId)}
              inputProps={{ "aria-label": "Device ID" }}
              sx={fieldSx}
            />
            {!isEdit && onFetch && (
            <SecondaryButton
              disabled={lookup?.status === "loading"}
              startIcon={<SearchRounded />}
              onClick={onFetch}
              sx={{
                minWidth: 0,
                minHeight: 40,
                whiteSpace: "nowrap",
                flexShrink: 0,
                // Outlined in teal: a step inside the form, not its main action
                // (Save Device in the footer is the filled button).
                color: designColors.tealDark,
                borderColor: designColors.tealDark,
              }}
            >
              {lookup?.status === "loading" ? "Fetching..." : "Fetch Device Information"}
            </SecondaryButton>
            )}
          </Mui.Stack>
        </Field>

        {lookup?.status === "loading" && <Mui.Stack direction="row" spacing={1} alignItems="center" role="status"><Mui.CircularProgress size={18} /><Mui.Typography sx={designType.helperText}>Fetching device information...</Mui.Typography></Mui.Stack>}
        {found && (
          <Mui.Box
            role="status"
            sx={{
              borderRadius: designRadii.banner,
              border: "1px solid #B7E4DB",
              overflow: "hidden",
            }}
          >
            <Mui.Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ p: 1.75, backgroundColor: designColors.tealTint }}
            >
              <CheckCircleRounded sx={{ fontSize: 30, color: designColors.tealDark }} />
              <Mui.Box>
                <Mui.Typography
                  sx={{ fontSize: 14, fontWeight: 600, color: designColors.tealDark }}
                >
                  Device found
                </Mui.Typography>
                <Mui.Typography sx={designType.helperText}>
                  Device ID verified successfully.
                </Mui.Typography>
              </Mui.Box>
            </Mui.Stack>
            <Mui.Box sx={{ p: 1.75 }}>
              <Mui.Stack spacing={0.5} sx={{ mb: 1.5 }}>
                {[["Server Device Name", lookup.name], ["Serial Number", lookup.serialNumber], ["Location", lookup.location], ["Direction", lookup.direction]].filter(([, value]) => value).map(([label, value]) => (
                  <Mui.Typography key={label} sx={designType.helperText}>{label}: {value}</Mui.Typography>
                ))}
              </Mui.Stack>
              <Mui.Typography
                sx={{ fontSize: 13, fontWeight: 500, color: designColors.slate700, mb: 1 }}
              >
                Detected Device Mode (from server)
              </Mui.Typography>
              <Mui.Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  display: "inline-flex",
                  px: 1.5,
                  py: 0.9,
                  minWidth: 180,
                  borderRadius: designRadii.control,
                  border: "1px solid #B7E4DB",
                  backgroundColor: designColors.tealTint,
                }}
              >
                <DetectedIcon sx={{ fontSize: 20, color: MODE_ICON_COLORS[lookup.mode] }} />
                <Mui.Typography
                  sx={{ fontSize: 14, fontWeight: 500, color: designColors.slate900 }}
                >
                  {modeTitle(lookup.mode)}
                </Mui.Typography>
              </Mui.Stack>
              <Mui.Typography sx={{ ...designType.helperText, mt: 1 }}>
                This device is configured as {modeTitle(lookup.mode)} in eBioServer /
                eSSL.
              </Mui.Typography>
            </Mui.Box>
          </Mui.Box>
        )}

        {lookup?.status === "notFound" && (
          <Mui.Stack
            role="alert"
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{
              p: 1.75,
              borderRadius: designRadii.banner,
              border: "1px solid #FECACA",
              backgroundColor: "#FEF2F2",
            }}
          >
            <ErrorOutlineRounded sx={{ fontSize: 28, color: DANGER }} />
            <Mui.Box>
              <Mui.Typography sx={{ fontSize: 14, fontWeight: 600, color: DANGER }}>
                Device not found
              </Mui.Typography>
              <Mui.Typography sx={designType.helperText}>
                No device with this ID exists on the server. Check the ID and
                fetch again.
              </Mui.Typography>
            </Mui.Box>
          </Mui.Stack>
        )}

        {lookup?.status === "unavailable" && (
          <Mui.Alert severity="info">
            Device information could not be retrieved. Fetch device information
            before saving this device.
          </Mui.Alert>
        )}
      </Step>

      <Step
        title="Configure Device"
        subtitle={isEdit ? "Set up how this device will be used in Cauver." : "Device details are filled from the server. Fetch the device information before saving."}
      >
        <Field label="Cauver Device Mode" required error={errors.mode}>
          <OptionCards
            label="Cauver Device Mode"
            options={modeOptions}
            value={isEdit ? draft.mode : found ? lookup.mode : ""}
            onChange={(value) => onChange("mode", value)}
            disabled
            icons={MODE_ICONS}
            iconColors={MODE_ICON_COLORS}
            badgeFor={found ? lookup.mode : undefined}
          />
          <Mui.Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1 }}>
              <InfoOutlined sx={{ fontSize: 16, color: designColors.slate500 }} />
              <Mui.Typography sx={designType.helperText}>
                {isEdit
                  ? "Device mode is supplied by the server and cannot be changed here."
                  : "Device mode is filled from fetched device information and cannot be changed here."}
              </Mui.Typography>
          </Mui.Stack>
        </Field>

        <Field
          label="Device Name"
          required
          error={errors.name}
          helperText={isEdit ? "A friendly name to identify the device (e.g. Main Gate, Office Entry)." : "Device name is supplied by the server."}
        >
          <Mui.TextField
            fullWidth
            size="small"
            value={draft.name}
            disabled={!isEdit && !found}
            InputProps={{ readOnly: !isEdit }}
            onChange={(event) => onChange("name", event.target.value)}
            error={Boolean(errors.name)}
            inputProps={{ "aria-label": "Device Name" }}
            sx={fieldSx}
          />
        </Field>

        <Field label="Device Type" required error={errors.purpose}>
          <OptionCards
            label="Device Type"
            options={DEVICE_PURPOSES}
            value={draft.purpose}
            onChange={(value) => onChange("purpose", value)}
            icons={PURPOSE_ICONS}
            disabled={!isEdit && !found}
          />
        </Field>

        <Field
          label="Device Location (Optional)"
          helperText={isEdit ? "Physical location of the device (e.g. Branch, Site, Gate)." : "Device location is supplied by the server."}
        >
          <Mui.TextField
            fullWidth
            size="small"
            value={draft.location}
            disabled={!isEdit && !found}
            InputProps={{ readOnly: !isEdit }}
            onChange={(event) => onChange("location", event.target.value)}
            inputProps={{ "aria-label": "Device Location" }}
            sx={fieldSx}
          />
        </Field>
      </Step>

    </Mui.Stack>
  );
};

export default AddDeviceForm;
